import {
  AddIcon,
  ChevronRightIcon,
  InfoIcon,
  InfoOutlineIcon,
} from '@chakra-ui/icons'
import {
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  Button,
  chakra,
  HStack,
  Icon,
  IconButton,
  Image,
  InputGroup,
  InputRightAddon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  Text as ChakraText,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
  Link as ChakraLink,
} from '@chakra-ui/react'
import { Base64 } from 'js-base64'
import _ from 'lodash'
import { ReactElement, useCallback } from 'react'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import type { IconType } from 'react-icons'
import {
  AiFillHome,
  AiOutlineUnorderedList,
  AiOutlineUpload,
} from 'react-icons/ai'
import { BsSortDownAlt, BsSortUpAlt, BsThreeDots } from 'react-icons/bs'
import { IoClose, IoGrid, IoSettingsOutline } from 'react-icons/io5'
import {
  MdCollections,
  MdOutlineRefresh,
  MdOutlineShare,
  MdWidgets,
} from 'react-icons/md'
import { RiFolderFill } from 'react-icons/ri'
import { useRouteMatch } from 'react-router'
import { Link, useLocation } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { PermissionsEnum } from '../../../utils'
import { fileTypeOptionsMapping } from '../../../utils/mediaLibrary'
import { isRestrictedUser } from '../../../utils/roles'
import {
  ACLPermissionEnum,
  FileType,
  MLSortDirection,
  MLSortParams,
  MLViewType,
} from '../../constants'
import { useGetQueryParams } from '../../hooks/useGetQueryParams'
import useToast from '../../hooks/useToast'
import type {
  ListSavedSearchSuccessResponse,
  MediaLibrarySearchCollectionResponse,
  ViewableParentSuccessResponse,
} from '../../interfaces'
import type { MediaLibrarySearchFolderResponse } from '../../interfaces/index'
import { MLContext, NavBarContext, UserContext } from '../../providers'
import { MediaLibraryActions } from '../../providers/MLProvider'
import CreateMediaCollection from '../MediaCollections/CreateMediaCollection'
import PermissionGuard from '../PermissionGuard'
import StickyShadow from '../StickyShadow'
import { ShareAssetModal, ShareMediaCollectionModal } from './MediaOperations'
import NewButton from './NewButton'
import { FeaturesEnum } from '../../../utils/features'
import { addLeadingSlash, addTrailingSlash } from '../../../utils/urlHelpers'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import { HiOutlineBookmark } from 'react-icons/hi'
import SaveSearchModal from '../SavedSearch/SaveSearchModal'
import ManageSavedSearchModal from '../SavedSearch/ManageSavedSearchModal'
import { HiMiniBookmark } from 'react-icons/hi2'
import { IoIosArrowRoundBack } from 'react-icons/io'

interface Props {
  resetSearch(): void
}
interface Crumb {
  label: string
  href: string
  icon: IconType
  folderPath?: string
}

const SaveSearchButton = ({ onClick }: { onClick(): void }) => {
  const { t } = useTranslation()
  return (
    <PermissionGuard
      permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
    >
      {(hasPermission: boolean) =>
        hasPermission ? (
          <Button
            variant="link"
            color="appClearBlue"
            leftIcon={<HiOutlineBookmark size="16px" />}
            iconSpacing="4px"
            onClick={onClick}
            style={{ marginLeft: '16px' }}
          >
            {t('save_this_search', 'Save this search')}
          </Button>
        ) : (
          <></>
        )
      }
    </PermissionGuard>
  )
}

const SavedSearchBreadcrumb = ({
  savedSearch,
  getAndSetSavedSearches,
  resetSearch,
}: {
  savedSearch: ListSavedSearchSuccessResponse
  getAndSetSavedSearches: () => void
  resetSearch: () => void
}) => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const {
    isOpen: isOpenManageSavedSearchModal,
    onOpen: onOpenManageSavedSearchModal,
    onClose: onCloseManageSavedSearchModal,
  } = useDisclosure()

  const truncatedName =
    savedSearch.name && savedSearch.name.length > 250
      ? savedSearch.name.slice(0, 250) + '...'
      : savedSearch.name

  return (
    <>
      <HStack spacing="4px" maxW="500px">
        <IconButton
          aria-label={t('back', 'Back')}
          variant="link"
          icon={<IoIosArrowRoundBack color="#3b485e" />}
          borderRadius="4px"
          border="1px solid #E6E7EA"
          minWidth="24px"
          height="24px"
          onClick={resetSearch}
          mr="4px"
        />
        <Icon as={HiMiniBookmark} boxSize="16px" color={colors.appSlate} />
        <Tooltip label={truncatedName} hasArrow>
          <Text fontWeight="500" color={colors.appSlate} isTruncated>
            {savedSearch.name}
          </Text>
        </Tooltip>
        <PermissionGuard
          permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
        >
          {(hasPermission: boolean) => {
            const shouldShow =
              !isRestrictedUser(user?.roles) ||
              user?.id === savedSearch.createdBy.userId
            return hasPermission && shouldShow ? (
              <Tooltip
                label={t('manage_saved_search', 'Manage saved search')}
                hasArrow
              >
                <IconButton
                  aria-label={t('manage_saved_search', 'Manage saved search')}
                  icon={<IoSettingsOutline />}
                  variant="link"
                  minWidth="16px"
                  borderRadius="4px"
                  height="16px"
                  color="appSlate"
                  ml="4px"
                  onClick={e => {
                    e.stopPropagation()
                    onOpenManageSavedSearchModal()
                  }}
                />
              </Tooltip>
            ) : (
              <></>
            )
          }}
        </PermissionGuard>
      </HStack>
      {isOpenManageSavedSearchModal && (
        <ManageSavedSearchModal
          name={savedSearch.name}
          url={savedSearch.url}
          searchId={savedSearch.searchId}
          isPrivate={savedSearch.isPrivate}
          createdBy={savedSearch.createdBy}
          isOpen={isOpenManageSavedSearchModal}
          onClose={onCloseManageSavedSearchModal}
          getAndSetSavedSearches={getAndSetSavedSearches}
        />
      )}
    </>
  )
}

const HeaderToolbar = ({ resetSearch }: Props): ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { isPublicLink, urlPrefix, clientInfo } = useContext(
    PublicLinksInfoContext,
  )
  const { savedSearches, setSavedSearches } = useContext(NavBarContext)
  const [state, dispatch] = useContext(MLContext)
  const { user } = useContext(UserContext)
  const clientNumber = user?.clientNumber ?? clientInfo?.clientNumber
  const location = useLocation()
  const [mlWidgetInsertButtonDisable, setMlWidgetInsertButtonDisable] =
    useState<boolean>(false)
  const [
    mlWidgetInsertButtonDisableTooltip,
    setMlWidgetInsertButtonDisableTooltip,
  ] = useState<any>(null)

  const {
    folderPath,
    currentMediaCollection,
    sortDescriptors: { sortParam, sortDirection },
    view,
    loadingMediaCollections,
    viewableParent,
    loadingViewableParent,
    mediaCollectionRoute,
    selectedFolders,
    selectedMediaCollections,
    selectedBreadcrumbFolder,
    selectedBreadcrumbMediaCollection,
    folderPermission,
    refetch,
    refetchFiles,
    refetchFolders,
    refetchMediaCollections,
    refetchDrawerFolders,
    refetchViewableParent,
    refetchFolderPermission,
  } = state

  const {
    url,
    params: { folderPathBase64 },
  } = useRouteMatch<{ folderPathBase64?: string }>()

  const queryFromUrl = useGetQueryParams()

  const [crumbs, setCrumbs] = useState<Crumb[]>([])

  const [isBlur, setIsBlur] = useState<boolean>(false)

  const [shareableAsset, setShareableAsset] = useState<string>('')

  const [shareFolderResponse, setShareFolderResponse] =
    useState<MediaLibrarySearchFolderResponse>()

  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const initialFocusRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!loadingViewableParent) {
      let pathBase: string
      if (currentMediaCollection) {
        pathBase = url.replace(
          folderPathBase64
            ? `/${currentMediaCollection}/${folderPathBase64}`
            : new RegExp(`/${currentMediaCollection}/?`, 'u'),
          '',
        )
      } else {
        pathBase = url.replace(`/${folderPathBase64}`, '')
      }
      let viewableFolderPath = folderPath
      let unViewableBasePath = ''
      if (
        viewableParent &&
        !_.isUndefined((viewableParent as ViewableParentSuccessResponse).asset)
      ) {
        const index = (
          viewableParent as ViewableParentSuccessResponse
        ).asset?.assetPath.lastIndexOf('/')
        if (index) {
          viewableFolderPath = folderPath.slice(
            index,
            viewableFolderPath.length,
          )
          unViewableBasePath = folderPath.slice(0, index)
        }
      }
      const [, ...pathComponents] =
        viewableFolderPath !== '/' ? viewableFolderPath.split('/') : []
      // eslint-disable-next-line prefer-named-capture-group
      if (mediaCollectionRoute) {
        const crumbsArr: Crumb[] = [
          {
            label: 'All collections',
            href: queryFromUrl.includes('view=LIST')
              ? `${pathBase}?view=LIST`
              : pathBase,
            icon: AiFillHome,
          },
        ]
        if (
          currentMediaCollection &&
          (viewableParent as ViewableParentSuccessResponse).mediaCollection
            ?.name
        ) {
          crumbsArr.push(
            {
              label:
                (viewableParent as ViewableParentSuccessResponse)
                  .mediaCollection?.name ?? '',
              href: `${pathBase}/${currentMediaCollection}/${
                queryFromUrl as string
              }`,
              icon: MdCollections,
              folderPath: '/',
            },
            ...pathComponents.map((x, i) => {
              const pathBase64 = Base64.encode(
                `${unViewableBasePath}/${pathComponents
                  .slice(0, i + 1)
                  .join('/')}`,
                true,
              )
              return {
                label: x,
                href: `${pathBase}/${currentMediaCollection}/${pathBase64}${
                  queryFromUrl as string
                }`,
                icon: RiFolderFill,
                folderPath: pathBase64,
              }
            }),
          )
        }
        setCrumbs(crumbsArr)
      } else {
        const crumbsArr = [
          {
            label: 'Home',
            href: `${pathBase}${queryFromUrl as string}`,
            icon: AiFillHome,
          },
        ]
        if (
          (viewableParent as ViewableParentSuccessResponse).asset?.assetPath
        ) {
          crumbsArr.push(
            ...pathComponents.map((x, i) => {
              const pathBase64 = Base64.encode(
                `${unViewableBasePath}/${pathComponents
                  .slice(0, i + 1)
                  .join('/')}`,
                true,
              )
              return {
                label: x,
                href: `${pathBase}/${pathBase64}${queryFromUrl as string}`,
                icon: RiFolderFill,
                folderPath: pathBase64,
              }
            }),
          )
        }
        setCrumbs(crumbsArr)
      }
    }
  }, [
    folderPath,
    viewableParent,
    currentMediaCollection,
    loadingViewableParent,
    queryFromUrl,
  ])

  useEffect(() => {
    if (
      selectedBreadcrumbFolder &&
      !selectedFolders
        .map(x => x.folderId)
        .includes(selectedBreadcrumbFolder.folderId)
    ) {
      dispatch({
        type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_FOLDER,
        payload: {
          folder: undefined,
        },
      })
    }
  }, [selectedFolders, selectedBreadcrumbFolder])

  useEffect(() => {
    if (
      selectedBreadcrumbMediaCollection &&
      !selectedMediaCollections
        .map(x => x.mediaCollectionId)
        .includes(selectedBreadcrumbMediaCollection.mediaCollectionId)
    ) {
      dispatch({
        type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_MEDIA_COLLECTION,
        payload: {
          mediaCollection: undefined,
        },
      })
    }
  }, [selectedMediaCollections, selectedBreadcrumbMediaCollection])

  useEffect(() => {
    const blur: boolean = crumbs.slice(1, crumbs.length - 1).filter(data => {
      if (
        data?.folderPath === '/' &&
        selectedBreadcrumbMediaCollection?.name === data.label
      ) {
        return true
      }
      if (!data?.folderPath || !selectedBreadcrumbFolder?.folderPath) {
        return false
      }
      return (
        data?.folderPath ===
        Base64.encode(selectedBreadcrumbFolder.folderPath, true)
      )
    }).length
      ? false
      : true

    setIsBlur(blur)
  }, [selectedBreadcrumbFolder, selectedBreadcrumbMediaCollection, crumbs])

  useEffect(() => {
    if (isBlur) {
      initialFocusRef.current?.focus()
    }
  }, [isBlur, initialFocusRef.current])

  useEffect(() => {
    if (isPublicLink) {
      return
    }
    const lastBreadcrumb = crumbs[crumbs.length - 1]
    if (
      lastBreadcrumb?.folderPath === '/' &&
      mediaCollectionRoute &&
      currentMediaCollection &&
      (viewableParent as ViewableParentSuccessResponse)?.mediaCollection
    ) {
      const mediaCollection = (viewableParent as ViewableParentSuccessResponse)
        .mediaCollection
      if (
        !mediaCollection?.permission ||
        mediaCollection.permission === ACLPermissionEnum.MANAGE
      ) {
        setShareableAsset('COLLECTION')
        return
      }
    } else if (
      lastBreadcrumb?.folderPath &&
      lastBreadcrumb.folderPath !== '/' &&
      user?.pricingPlan.featuresList.includes(FeaturesEnum.ACL_AND_COLLECTIONS)
    ) {
      if (
        !(
          isRestrictedUser(user?.roles) &&
          folderPermission !== ACLPermissionEnum.MANAGE
        )
      ) {
        setShareableAsset('FOLDER')
        return
      }
    }

    setShareableAsset('')
  }, [
    crumbs,
    mediaCollectionRoute,
    currentMediaCollection,
    viewableParent,
    folderPermission,
    user,
    isPublicLink,
  ])

  useEffect(() => {
    if (window.mlWidgetSettings?.multiple === false) {
      setMlWidgetInsertButtonDisable(state.selectedFiles.length > 1)
      if (state.selectedFiles.length > 1) {
        setMlWidgetInsertButtonDisableTooltip(
          t('only_one_file_allowed', 'Multiple file selection is not allowed'),
        )
      }
    } else if (
      window.mlWidgetSettings?.maxFiles &&
      window.mlWidgetSettings.maxFiles > 0
    ) {
      setMlWidgetInsertButtonDisable(
        state.selectedFiles.length > window.mlWidgetSettings.maxFiles,
      )
      const maxFiles = window.mlWidgetSettings.maxFiles
      setMlWidgetInsertButtonDisableTooltip(
        <Trans t={t} i18nKey="max_files_allowed">
          Maximum {{ maxFiles }} files allowed to be selected
        </Trans>,
      )
    }
  }, [state.selectedFiles, window.mlWidgetSettings])

  const shareFolderFunction = useCallback(
    async event => {
      if (isPublicLink) {
        return
      }
      const folder = (await request.get(
        `/api/clients/${user?.clientNumber}/access-control/breadcrumbs?folderPath=${folderPath}`,
      )) as MediaLibrarySearchFolderResponse
      if (request.error) {
        toast({
          description: t(
            'something_went_wrong',
            'Something went wrong, please try again after sometime',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }

      setShareFolderResponse(folder)
      dispatch([
        {
          type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_FOLDER,
          payload: {
            folder,
          },
        },
        {
          type: MediaLibraryActions.SELECT_FOLDER,
          payload: {
            folder,
            event,
            isBreadcrumb: true,
          },
        },
        {
          type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
        },
      ])
      onOpenShareAssetModal()
    },
    [folderPath, user, isPublicLink],
  )

  const getAndSetSavedSearches = async () => {
    const res = (await request.get(
      `/api/media-library/${user?.clientNumber}/saved-searches`,
    )) as ListSavedSearchSuccessResponse[]

    if (request.error) {
      toast({
        description: t(
          'error_in_getting_saved_searches',
          'Error in getting saved searches, please try again later.',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else {
      setSavedSearches(res)
    }
  }

  useEffect(() => {
    if (isPublicLink) {
      return
    }
    getAndSetSavedSearches()
  }, [])

  const {
    isOpen: isOpenCreateMediaCollectionModal,
    onOpen: onOpenCreateMediaCollectionModal,
    onClose: onCloseCreateMediaCollectionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenShareAssetModal,
    onOpen: onOpenShareAssetModal,
    onClose: onCloseShareAssetModal,
  } = useDisclosure()

  const {
    isOpen: isOpenShareMediaCollectionModal,
    onOpen: onOpenShareMediaCollectionModal,
    onClose: onCloseShareMediaCollectionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenSaveSearchModal,
    onOpen: onOpenSaveSearchModal,
    onClose: onCloseSaveSearchModal,
  } = useDisclosure()

  const BreadcrumbReplacement = useMemo<React.ReactElement | null>(() => {
    const FolderPathTooltip = React.memo(
      ({ path }: { path: string }): React.ReactElement => (
        <Tooltip label={path} hasArrow>
          {window.isMediaLibraryWidget === true ? (
            <ChakraLink
              as={Link}
              to={`/media-library/${Base64.encode(state.folderPath, true)}`}
              display="inline-block"
              maxWidth="15ch"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              overflow="hidden"
              verticalAlign="bottom"
              _focus={{ outline: 'none', boxShadow: 'none' }}
            >
              {path.split('/').pop() ?? ''}
            </ChakraLink>
          ) : (
            <ChakraText
              as={ChakraLink}
              isExternal
              href={`/dashboard/media-library/${Base64.encode(
                state.folderPath,
                true,
              )}`}
              display="inline-block"
              maxWidth="15ch"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              overflow="hidden"
              verticalAlign="bottom"
              _focus={{ outline: 'none', boxShadow: 'none' }}
            >
              {path.split('/').pop() ?? ''}
            </ChakraText>
          )}
        </Tooltip>
      ),
    )
    if (
      folderPath === '/' && !currentMediaCollection && mediaCollectionRoute
        ? state.searchQuery === ''
        : state.fileType === FileType.all &&
          state.nameValue === '' &&
          state.semantic === '' &&
          state.semanticUrl === '' &&
          state.searchQueryJson.filterGroups.length === 0 &&
          !state.isVersionIncludedInSearch &&
          state.isFolderRestrictedSearch === 'false'
    ) {
      return null
    }

    const getPathFromUrl = (url: string) => {
      try {
        const urlObj = new URL(url)
        const normalizedPath = urlObj.pathname.replace(
          /^\/(?:dashboard|media-library-widget)/,
          '',
        )
        return `${normalizedPath}${urlObj.search}${urlObj.hash}`
      } catch (e) {
        return url
      }
    }

    const currentUrl = window.isMediaLibraryWidget
      ? document
          .querySelector('iframe[title="ImageKit Embedded Media Library"]')
          ?.getAttribute('href') || window.location.href
      : window.location.href

    const matchingSearch = savedSearches.find(
      search => getPathFromUrl(search.url) === getPathFromUrl(currentUrl),
    )

    if (matchingSearch) {
      return (
        <SavedSearchBreadcrumb
          savedSearch={matchingSearch}
          getAndSetSavedSearches={getAndSetSavedSearches}
          resetSearch={resetSearch}
        />
      )
    }

    if (
      folderPath === '/' &&
      !currentMediaCollection &&
      mediaCollectionRoute &&
      state.nameValue &&
      state.searchQueryJson.filterGroups.length === 0
    ) {
      return (
        <HStack spacing="0.5em">
          <Trans t={t} i18nKey="media_collections_search_results">
            <ChakraText color={colors.appSlate}>
              <chakra.span>Search results for</chakra.span>{' '}
              <chakra.span fontWeight="medium">
                {{
                  mediaCollectionSearchQuery: state.nameValue,
                }}
              </chakra.span>{' '}
              <chakra.span>in Media collections</chakra.span>
            </ChakraText>
          </Trans>

          <Icon
            onClick={resetSearch}
            cursor="pointer"
            as={IoClose}
            boxSize="1.5rem"
            color={colors.appBattleshipGrey}
          />

          <SaveSearchButton onClick={onOpenSaveSearchModal} />
        </HStack>
      )
    }
    // Eg. `Search results for custom search filters`
    if (state.searchQueryJson.filterGroups.length > 0) {
      return (
        <HStack spacing="0.5em">
          {state.isFolderRestrictedSearch === 'true' ? (
            <Trans
              t={t}
              i18nKey="custom_search_filters_results_folder_restricted"
            >
              <ChakraText color={colors.appSlate} isTruncated>
                <chakra.span>Search results for</chakra.span>{' '}
                <chakra.span fontWeight="medium">
                  custom search filters
                </chakra.span>{' '}
                <chakra.span>
                  in <FolderPathTooltip path={state.folderPath} /> folder
                </chakra.span>
              </ChakraText>
            </Trans>
          ) : (
            <Trans t={t} i18nKey="custom_search_filters_results">
              <ChakraText color={colors.appSlate} isTruncated>
                <chakra.span>Search results for</chakra.span>{' '}
                <chakra.span fontWeight="medium">
                  custom search filters
                </chakra.span>{' '}
                <chakra.span>
                  {{
                    location:
                      folderPath === '/' &&
                      !currentMediaCollection &&
                      mediaCollectionRoute
                        ? 'in Media collection'
                        : 'in Media library',
                  }}
                </chakra.span>
              </ChakraText>
            </Trans>
          )}

          <Icon
            onClick={resetSearch}
            cursor="pointer"
            as={IoClose}
            boxSize="1.5rem"
            color={colors.appBattleshipGrey}
          />

          <SaveSearchButton onClick={onOpenSaveSearchModal} />
        </HStack>
      )
    }

    if (state.fileListError === 'Rate limit for dam-only-free plan exceeded') {
      return (
        <Text>
          You have reached the rate limit for the free plan.
          <br />
          Please{' '}
          <ChakraLink href="/dashboard/upgrade-plan">
            upgrade to a paid plan
          </ChakraLink>{' '}
          to continue using this feature.
        </Text>
      )
    }

    if (state.semantic) {
      return (
        <HStack spacing="0.5em">
          <Trans t={t} i18nKey="query_text_semantic_search_results">
            <ChakraText color={colors.appSlate}>
              <Text as="span">
                <Badge display="inline-block" variant="imagekitWhite" mr="2">
                  AI Powered
                </Badge>
              </Text>
              <Text as="span">Search results for</Text>{' '}
              <Text as="span" fontWeight="medium">
                {' '}
                {{ semantic: state.semantic }}
              </Text>{' '}
              {{
                fileVersionState: state.isVersionIncludedInSearch
                  ? 'in File versions (including files)'
                  : null,
              }}
              <Text as="span">in Media library</Text>
            </ChakraText>
          </Trans>

          <Icon
            onClick={resetSearch}
            cursor="pointer"
            as={IoClose}
            boxSize="1.5rem"
            color={colors.appBattleshipGrey}
          />

          <SaveSearchButton onClick={onOpenSaveSearchModal} />
        </HStack>
      )
    }

    if (state.semanticUrl) {
      return (
        <HStack
          spacing="0.5em"
          borderRadius="2xl"
          backgroundColor={
            state.fileListError ? 'appVeryLightPink' : 'appVeryLightBlue'
          }
          px="4px"
          py="2px"
        >
          {state.fileListError ? (
            <Icon as={InfoIcon} color="appWatermelon" boxSize="14px" />
          ) : (
            <Box
              h="20px"
              w="20px"
              backgroundImage={`url(${state.semanticUrl})`}
              backgroundPosition="center"
              backgroundSize="cover"
              backgroundRepeat="no-repeat"
              borderRadius="full"
              ml="-2px"
            />
          )}
          <Text
            color={state.fileListError ? 'appWatermelon' : 'appBlue'}
            fontSize="12px"
            as="span"
          >
            {state.fileListError
              ? state.fileListError
              : t('searching_by_image', 'Searching by image')}
          </Text>
          <Icon
            onClick={resetSearch}
            cursor="pointer"
            color={state.fileListError ? 'appWatermelon' : 'appBlue'}
            as={IoClose}
            boxSize="1.5rem"
            p="0.25rem"
          />

          <SaveSearchButton onClick={onOpenSaveSearchModal} />
        </HStack>
      )
    }

    // Eg. `Search results for "query text" in Videos`
    if (state.fileType !== FileType.all && state.nameValue) {
      return (
        <HStack spacing="0.5em">
          {state.isFolderRestrictedSearch === 'true' ? (
            <Trans
              t={t}
              i18nKey="query_text_file_type_search_results_folder_restricted"
            >
              <ChakraText color={colors.appSlate}>
                <chakra.span>Search results for</chakra.span>{' '}
                <chakra.span fontWeight="medium">
                  {{ nameValue: state.nameValue }}
                </chakra.span>{' '}
                {{
                  fileVersionState: state.isVersionIncludedInSearch
                    ? 'in File versions (including files)'
                    : null,
                }}
                <chakra.span fontWeight="medium">
                  {{ fileType: fileTypeOptionsMapping[state.fileType] }}
                </chakra.span>{' '}
                <chakra.span>
                  in <FolderPathTooltip path={state.folderPath} /> folder
                </chakra.span>
              </ChakraText>
            </Trans>
          ) : (
            <Trans t={t} i18nKey="query_text_file_type_search_results">
              <ChakraText color={colors.appSlate}>
                <chakra.span>Search results for</chakra.span>{' '}
                <chakra.span fontWeight="medium">
                  {{ nameValue: state.nameValue }}
                </chakra.span>{' '}
                {{
                  fileVersionState: state.isVersionIncludedInSearch
                    ? 'in File versions (including files)'
                    : null,
                }}
                <chakra.span fontWeight="medium">
                  {{ fileType: fileTypeOptionsMapping[state.fileType] }}
                </chakra.span>{' '}
                <chakra.span>in Media library</chakra.span>
              </ChakraText>
            </Trans>
          )}

          <Icon
            onClick={resetSearch}
            cursor="pointer"
            as={IoClose}
            boxSize="1.5rem"
            color={colors.appBattleshipGrey}
          />

          <SaveSearchButton onClick={onOpenSaveSearchModal} />
        </HStack>
      )
    }

    // Eg. `Search results for "query text"`
    if (state.nameValue) {
      return (
        <HStack spacing="0.5em">
          {state.isFolderRestrictedSearch === 'true' ? (
            <Trans t={t} i18nKey="query_text_search_results_folder_restricted">
              <ChakraText color={colors.appSlate}>
                <chakra.span>Search results for</chakra.span>{' '}
                <chakra.span fontWeight="medium">
                  {' '}
                  {{ nameValue: state.nameValue }}
                </chakra.span>{' '}
                {{
                  fileVersionState: state.isVersionIncludedInSearch
                    ? 'in File versions (including files)'
                    : null,
                }}
                <chakra.span>
                  in <FolderPathTooltip path={state.folderPath} /> folder
                </chakra.span>
              </ChakraText>
            </Trans>
          ) : (
            <Trans t={t} i18nKey="query_text_search_results">
              <ChakraText color={colors.appSlate}>
                <chakra.span>Search results for</chakra.span>{' '}
                <chakra.span fontWeight="medium">
                  {' '}
                  {{ nameValue: state.nameValue }}
                </chakra.span>{' '}
                {{
                  fileVersionState: state.isVersionIncludedInSearch
                    ? 'in File versions (including files)'
                    : null,
                }}
                <chakra.span>'in Media library'</chakra.span>
              </ChakraText>
            </Trans>
          )}

          <Icon
            onClick={resetSearch}
            cursor="pointer"
            as={IoClose}
            boxSize="1.5rem"
            color={colors.appBattleshipGrey}
          />

          <SaveSearchButton onClick={onOpenSaveSearchModal} />
        </HStack>
      )
    } else if (state.fileType !== FileType.all) {
      // Eg. `Search results for Videos`
      return (
        <HStack spacing="0.5em">
          {state.isFolderRestrictedSearch === 'true' ? (
            <Trans t={t} i18nKey="file_type_search_results_folder_restricted">
              <ChakraText color={colors.appSlate}>
                <chakra.span>Search results for</chakra.span>{' '}
                <chakra.span fontWeight="medium">
                  {{ fileTypeOption: fileTypeOptionsMapping[state.fileType] }}
                </chakra.span>{' '}
                {{
                  fileVersionState: state.isVersionIncludedInSearch
                    ? 'in File versions (including files)'
                    : null,
                }}
                <chakra.span>
                  in <FolderPathTooltip path={state.folderPath} /> folder
                </chakra.span>
              </ChakraText>
            </Trans>
          ) : (
            <Trans t={t} i18nKey="file_type_search_results">
              <ChakraText color={colors.appSlate}>
                <chakra.span>Search results for</chakra.span>{' '}
                <chakra.span fontWeight="medium">
                  {{ fileTypeOption: fileTypeOptionsMapping[state.fileType] }}
                </chakra.span>{' '}
                {{
                  fileVersionState: state.isVersionIncludedInSearch
                    ? 'in File versions (including files)'
                    : null,
                }}
                <chakra.span>in Media library</chakra.span>
              </ChakraText>
            </Trans>
          )}

          <Icon
            onClick={resetSearch}
            cursor="pointer"
            as={IoClose}
            boxSize="1.5rem"
            color={colors.appBattleshipGrey}
          />

          <SaveSearchButton onClick={onOpenSaveSearchModal} />
        </HStack>
      )
    } else if (state.isVersionIncludedInSearch) {
      return (
        <HStack spacing="0.5em">
          {state.isFolderRestrictedSearch === 'true' ? (
            <Trans
              t={t}
              i18nKey="file_version_search_results_folder_restricted"
            >
              <ChakraText color={colors.appSlate}>
                <chakra.span>Search results for</chakra.span>{' '}
                <chakra.span fontWeight="medium">
                  File versions (including files)
                </chakra.span>{' '}
                <chakra.span>
                  in <FolderPathTooltip path={state.folderPath} /> folder
                </chakra.span>
              </ChakraText>
            </Trans>
          ) : (
            <Trans t={t} i18nKey="file_version_search_results">
              <ChakraText color={colors.appSlate}>
                <chakra.span>Search results for</chakra.span>{' '}
                <chakra.span fontWeight="medium">
                  File versions (including files)
                </chakra.span>{' '}
                <chakra.span>in Media library</chakra.span>
              </ChakraText>
            </Trans>
          )}

          <Icon
            onClick={resetSearch}
            cursor="pointer"
            as={IoClose}
            boxSize="1.5rem"
            color={colors.appBattleshipGrey}
          />

          <SaveSearchButton onClick={onOpenSaveSearchModal} />
        </HStack>
      )
    }

    return null
  }, [state, savedSearches])

  const handleClick: React.MouseEventHandler<HTMLElement> = event => {
    if (event.detail > 1) {
      return
    }
    void state.abortFilesRequest()
    void state.abortFoldersRequest()
  }

  const handleDoubleClick: React.MouseEventHandler<HTMLElement> = event => {
    event.preventDefault()
  }

  const handleContextMenuEvent = async (
    event: React.MouseEvent<HTMLAnchorElement>,
    x: Crumb,
  ): Promise<void> => {
    event.preventDefault()

    if (
      x.folderPath === '/' &&
      mediaCollectionRoute &&
      currentMediaCollection &&
      (viewableParent as ViewableParentSuccessResponse)?.mediaCollection
    ) {
      dispatch([
        {
          type: MediaLibraryActions.SELECT_MEDIA_COLLECTION,
          payload: {
            mediaCollection: (viewableParent as ViewableParentSuccessResponse)
              .mediaCollection as MediaLibrarySearchCollectionResponse,
            event,
          },
        },
        {
          type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_MEDIA_COLLECTION,
          payload: {
            mediaCollection: (viewableParent as ViewableParentSuccessResponse)
              .mediaCollection as MediaLibrarySearchCollectionResponse,
          },
        },
        {
          type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
        },
        {
          type: MediaLibraryActions.OPEN_CONTEXT_MENU,
          payload: { x: event.pageX, y: event.pageY },
        },
      ])
    } else if (x?.folderPath && x.folderPath !== '/') {
      const folder = (await request.get(
        `${urlPrefix}api/clients/${clientNumber}/access-control/breadcrumbs?folderPath=${Base64.decode(
          x.folderPath,
        )}`,
      )) as MediaLibrarySearchFolderResponse
      if (request.error) {
        toast({
          description: t(
            'something_went_wrong',
            'Something went wrong, please try again after sometime',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }

      dispatch([
        {
          type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_FOLDER,
          payload: {
            folder,
          },
        },
        {
          type: MediaLibraryActions.SELECT_FOLDER,
          payload: {
            folder,
            event,
            isBreadcrumb: true,
          },
        },
        {
          type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
        },
        {
          type: MediaLibraryActions.OPEN_CONTEXT_MENU,
          payload: { x: event.pageX, y: event.pageY },
        },
      ])
    }
  }

  const hideSortAndOtherOptions = useMemo<boolean>(() => {
    let _hideSortAndOtherOptions = false

    if (isPublicLink) {
      if (mediaCollectionRoute) {
        if (!currentMediaCollection) {
          _hideSortAndOtherOptions = true
        }
      } else if (folderPath === '/') {
        _hideSortAndOtherOptions = true
      }
    }

    return _hideSortAndOtherOptions
  }, [isPublicLink, currentMediaCollection, mediaCollectionRoute, folderPath])

  const shadowRef = useRef<HTMLDivElement>(null)

  return (
    <>
      <HStack
        ref={shadowRef}
        justifyContent="space-between"
        position="sticky"
        top="calc(35px + 2rem + 1px)" // main height (header action bar) + padding*2 + borderBottomWidth
        background="white"
        paddingY="0.5rem"
        paddingX="1.5rem"
        zIndex="docked"
        height={'49px'}
        minHeight={'49px'}
        onClick={event => event.stopPropagation()}
      >
        {BreadcrumbReplacement ??
          (!(loadingViewableParent || loadingMediaCollections) ? (
            <HStack padding={0} margin={0} spacing={0}>
              <Breadcrumb
                spacing="8px"
                separator={<ChevronRightIcon color="appSlate" />}
              >
                {Number(crumbs.length) <= 4 ? (
                  crumbs.map(x => (
                    <BreadcrumbItem key={x.href}>
                      <Icon
                        marginTop={1}
                        as={x.icon}
                        boxSize={5}
                        color="appSlate"
                        mr={2}
                        mb={1}
                      />
                      <Tooltip key={x.href} label={x.label}>
                        <BreadcrumbLink
                          as={Link}
                          to={x.href}
                          onClick={handleClick}
                          onDoubleClick={handleDoubleClick}
                          color="appSlate"
                          fontWeight="medium"
                          maxW="8em"
                          isTruncated
                          onContextMenu={event =>
                            handleContextMenuEvent(event, x)
                          }
                          borderRadius="lg"
                          paddingX={1}
                          backgroundColor={
                            (x?.folderPath &&
                              selectedBreadcrumbFolder?.folderPath &&
                              x.folderPath ===
                                Base64.encode(
                                  selectedBreadcrumbFolder.folderPath,
                                  true,
                                )) ||
                            selectedBreadcrumbMediaCollection?.name === x.label
                              ? colors.appBlueyGrey
                              : ''
                          }
                          _focus={{
                            outline: 'none',
                          }}
                        >
                          {x.label}
                        </BreadcrumbLink>
                      </Tooltip>
                    </BreadcrumbItem>
                  ))
                ) : (
                  <>
                    <BreadcrumbItem key={crumbs[0].href}>
                      <Icon
                        as={crumbs[0].icon}
                        boxSize={5}
                        color="appSlate"
                        mr={2}
                        mb={1}
                      />
                      <BreadcrumbLink
                        as={Link}
                        to={crumbs[0].href}
                        color="appSlate"
                        fontWeight="medium"
                        onClick={handleClick}
                        onDoubleClick={handleDoubleClick}
                        maxW="8em"
                        paddingX={1}
                        borderRadius="lg"
                        _focus={{
                          outline: 'none',
                        }}
                        isTruncated
                      >
                        {crumbs[0].label}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator
                      margin="0em 0.5em 0.5em 0.5em"
                      as={ChevronRightIcon}
                    />
                    <BreadcrumbItem>
                      <BreadcrumbLink fontWeight="medium">
                        <Popover trigger="click" closeOnBlur={isBlur}>
                          <PopoverTrigger>
                            <IconButton
                              aria-label="More Folders"
                              icon={<Icon as={BsThreeDots} />}
                              variant="link"
                              boxShadow="none"
                              marginX="0"
                              paddingX="0"
                              minW="min-content"
                              color="appSlate"
                            />
                          </PopoverTrigger>
                          <PopoverContent
                            maxWidth="unset"
                            width="unset"
                            maxH="200px"
                            overflowY="auto"
                            _focus={{
                              outline: 'none',
                            }}
                            ref={initialFocusRef}
                          >
                            <PopoverArrow />
                            <PopoverBody padding="0">
                              <VStack paddingY={2} paddingX={1}>
                                {crumbs
                                  .slice(1, crumbs.length - 1)
                                  .map(data => (
                                    <Box
                                      key={data.href}
                                      as={Link}
                                      to={data.href}
                                      paddingX={1}
                                      paddingY={1}
                                      width="100%"
                                      _hover={{
                                        backgroundColor: 'appGrey',
                                      }}
                                      onClick={handleClick}
                                      onDoubleClick={handleDoubleClick}
                                      onContextMenu={(
                                        event: React.MouseEvent<
                                          HTMLDivElement,
                                          MouseEvent
                                        > &
                                          React.MouseEvent<
                                            HTMLAnchorElement,
                                            MouseEvent
                                          >,
                                      ) => {
                                        void handleContextMenuEvent(event, data)
                                      }}
                                      fontWeight="normal"
                                      color="appDarkIndigo"
                                      borderRadius="lg"
                                      backgroundColor={
                                        (data?.folderPath &&
                                          selectedBreadcrumbFolder?.folderPath &&
                                          data.folderPath ===
                                            Base64.encode(
                                              selectedBreadcrumbFolder.folderPath,
                                              true,
                                            )) ||
                                        selectedBreadcrumbMediaCollection?.name ===
                                          data.label
                                          ? colors.appBlueyGrey
                                          : ''
                                      }
                                      _focus={{
                                        outline: 'none',
                                      }}
                                    >
                                      {data.label}
                                    </Box>
                                  ))}
                              </VStack>
                            </PopoverBody>
                          </PopoverContent>
                        </Popover>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator
                      margin="0em 0.5em 0.5em 0.5em"
                      as={ChevronRightIcon}
                    />
                    <BreadcrumbItem key={crumbs[crumbs.length - 1].href}>
                      <Icon
                        as={crumbs[crumbs.length - 1].icon}
                        boxSize={5}
                        color="appSlate"
                        mr={2}
                        mb={1}
                      />
                      <Tooltip label={crumbs[crumbs.length - 1].label}>
                        <BreadcrumbLink
                          as={Link}
                          to={crumbs[crumbs.length - 1].href}
                          color="appSlate"
                          fontWeight="medium"
                          onClick={handleClick}
                          onDoubleClick={handleDoubleClick}
                          onContextMenu={event =>
                            handleContextMenuEvent(
                              event,
                              crumbs[crumbs.length - 1],
                            )
                          }
                          borderRadius="lg"
                          paddingX={1}
                          backgroundColor={
                            (crumbs[crumbs.length - 1]?.folderPath &&
                              selectedBreadcrumbFolder?.folderPath &&
                              crumbs[crumbs.length - 1].folderPath ===
                                Base64.encode(
                                  selectedBreadcrumbFolder.folderPath,
                                  true,
                                )) ||
                            selectedBreadcrumbMediaCollection?.name ===
                              crumbs[crumbs.length - 1].label
                              ? colors.appBlueyGrey
                              : ''
                          }
                          _focus={{
                            outline: 'none',
                          }}
                          maxW="8em"
                          isTruncated
                        >
                          {crumbs[crumbs.length - 1].label}
                        </BreadcrumbLink>
                      </Tooltip>
                    </BreadcrumbItem>
                  </>
                )}
              </Breadcrumb>
              {shareableAsset ? (
                <Tooltip label={t('share', 'Share')}>
                  <IconButton
                    aria-label="Share"
                    icon={
                      <Icon
                        as={MdOutlineShare}
                        boxSize={5}
                        marginBottom={1.5}
                      />
                    }
                    onClick={async event => {
                      if (shareableAsset === 'COLLECTION') {
                        dispatch([
                          {
                            type: MediaLibraryActions.SELECT_MEDIA_COLLECTION,
                            payload: {
                              mediaCollection: (
                                viewableParent as ViewableParentSuccessResponse
                              )
                                .mediaCollection as MediaLibrarySearchCollectionResponse,
                              event,
                            },
                          },
                          {
                            type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_MEDIA_COLLECTION,
                            payload: {
                              mediaCollection: (
                                viewableParent as ViewableParentSuccessResponse
                              )
                                .mediaCollection as MediaLibrarySearchCollectionResponse,
                            },
                          },
                          {
                            type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                          },
                        ])
                        onOpenShareMediaCollectionModal()
                      } else if (shareableAsset === 'FOLDER') {
                        void shareFolderFunction(event)
                      }
                    }}
                    variant="link"
                  />
                </Tooltip>
              ) : null}
            </HStack>
          ) : (
            <Box w="50%" />
          ))}
        {(viewableParent as ViewableParentSuccessResponse).asset ||
        (viewableParent as ViewableParentSuccessResponse).mediaCollection ? (
          <HStack spacing={2}>
            {hideSortAndOtherOptions ? null : (
              <Tooltip label={t('refresh', 'Refresh')}>
                <IconButton
                  aria-label="Refresh"
                  icon={<Icon as={MdOutlineRefresh} boxSize="1.5rem" />}
                  onClick={async () => {
                    dispatch([
                      {
                        type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                      },
                      {
                        type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
                      },
                    ])
                    await refetchFolderPermission()
                    await refetchViewableParent()
                    if (!isPublicLink) {
                      await refetch()
                      await refetchDrawerFolders()
                    } else {
                      if (mediaCollectionRoute) {
                        await refetchMediaCollections()
                      } else {
                        await refetchFiles()
                        await refetchFolders()
                      }
                    }
                  }}
                  variant="link"
                />
              </Tooltip>
            )}
            {view === MLViewType.GRID ? (
              <Tooltip label={t('switch_to_list_view', 'Switch to list view')}>
                <IconButton
                  aria-label="List View"
                  icon={<Icon as={AiOutlineUnorderedList} boxSize={6} />}
                  onClick={() => {
                    dispatch({
                      type: MediaLibraryActions.SET_VIEW,
                      payload: MLViewType.LIST,
                    })
                  }}
                  variant="link"
                />
              </Tooltip>
            ) : null}
            {view === MLViewType.LIST ? (
              <Tooltip label={t('switch_to_grid_view', 'Switch to grid view')}>
                <IconButton
                  aria-label="Grid View"
                  icon={<Icon as={IoGrid} boxSize={6} />}
                  onClick={() => {
                    dispatch({
                      type: MediaLibraryActions.SET_VIEW,
                      payload: MLViewType.GRID,
                    })
                  }}
                  variant="link"
                />
              </Tooltip>
            ) : null}
            {hideSortAndOtherOptions ? null : (
              <InputGroup>
                {isPublicLink ||
                (!currentMediaCollection && mediaCollectionRoute) ? (
                  <Select
                    variant="addon"
                    value={sortParam}
                    onChange={e => {
                      dispatch({
                        type: MediaLibraryActions.SET_SORT_PARAM,
                        payload: e.target.value as MLSortParams,
                      })
                    }}
                  >
                    <option value={MLSortParams.CREATED}>Creation Date</option>
                    <option value={MLSortParams.NAME}>Name</option>
                    ...
                    {!isPublicLink && (
                      <option value={MLSortParams.RELEVANCE}>Relevance</option>
                    )}
                  </Select>
                ) : (
                  <Select
                    variant="addon"
                    value={sortParam}
                    onChange={e => {
                      dispatch({
                        type: MediaLibraryActions.SET_SORT_PARAM,
                        payload: e.target.value as MLSortParams,
                      })
                    }}
                  >
                    <option value={MLSortParams.CREATED}>Creation Date</option>
                    <option value={MLSortParams.UPDATED}>
                      Last Updated Date
                    </option>
                    <option value={MLSortParams.NAME}>Name</option>
                    <option value={MLSortParams.HEIGHT}>Image Height</option>
                    <option value={MLSortParams.WIDTH}>Image Width</option>
                    <option value={MLSortParams.SIZE}>File Size</option>
                    <option value={MLSortParams.RELEVANCE}>Relevance</option>
                  </Select>
                )}
                <InputRightAddon paddingX={2}>
                  {sortDirection === MLSortDirection.DESC ? (
                    <Tooltip
                      label={t('sort_ascending', 'Sort in ascending order')}
                    >
                      <IconButton
                        aria-label="Sort Ascending"
                        variant="link"
                        icon={<Icon as={BsSortDownAlt} boxSize={6} />}
                        onClick={() => {
                          dispatch({
                            type: MediaLibraryActions.SET_SORT_DIRECTION,
                            payload: MLSortDirection.ASC,
                          })
                        }}
                      />
                    </Tooltip>
                  ) : null}
                  {sortDirection === MLSortDirection.ASC ? (
                    <Tooltip
                      label={t('sort_descending', 'Sort in descending order')}
                    >
                      <IconButton
                        aria-label="Sort Descending"
                        variant="link"
                        icon={<Icon as={BsSortUpAlt} boxSize={6} />}
                        onClick={() => {
                          dispatch({
                            type: MediaLibraryActions.SET_SORT_DIRECTION,
                            payload: MLSortDirection.DESC,
                          })
                        }}
                      />
                    </Tooltip>
                  ) : null}
                </InputRightAddon>
              </InputGroup>
            )}
            <PermissionGuard
              permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
            >
              {(hasPermission: boolean) =>
                hasPermission && !isPublicLink ? (
                  !currentMediaCollection && mediaCollectionRoute ? (
                    <Button
                      leftIcon={<AddIcon />}
                      onClick={onOpenCreateMediaCollectionModal}
                      colorScheme="appBlue"
                      paddingX="1.8em"
                      className="create-collection-button"
                    >
                      {t('new', 'New')}
                    </Button>
                  ) : (
                    <NewButton />
                  )
                ) : (
                  <></>
                )
              }
            </PermissionGuard>
            {window.isMediaLibraryWidget ? (
              <Tooltip
                label={
                  mlWidgetInsertButtonDisable
                    ? mlWidgetInsertButtonDisableTooltip || ''
                    : ''
                }
              >
                <Box>
                  <Button
                    colorScheme="appBlue"
                    leftIcon={<AiOutlineUpload />}
                    variant="outline"
                    isDisabled={
                      state.selectedFiles.length === 0 ||
                      mlWidgetInsertButtonDisable
                    }
                    onClick={e => {
                      e.stopPropagation()
                      if (
                        window.self !== window.top &&
                        window.widgetHost !== 'uxpHost'
                      ) {
                        window.parent.postMessage(
                          {
                            eventType: 'INSERT',
                            data: JSON.parse(
                              JSON.stringify(state.selectedFiles),
                            ),
                          },
                          window.widgetHost ?? document.referrer,
                        )
                      } else if (
                        window.widgetHost === 'uxpHost' &&
                        window.uxpHost
                      ) {
                        window.uxpHost.postMessage(
                          {
                            eventType: 'INSERT',
                            data: JSON.parse(
                              JSON.stringify(state.selectedFiles),
                            ),
                          },
                          window.widgetHost ?? document.referrer,
                        )
                      } else {
                        console.log(
                          '[DEBUG]',
                          'Simulating response to parent',
                          {
                            eventType: 'INSERT',
                            data: state.selectedFiles,
                            widgetHost: window.widgetHost ?? document.referrer,
                          },
                        )
                      }
                    }}
                  >
                    {t('insert', 'Insert')}
                  </Button>
                </Box>
              </Tooltip>
            ) : null}
          </HStack>
        ) : null}
      </HStack>
      <StickyShadow shadowRef={shadowRef} />
      <CreateMediaCollection
        isOpen={isOpenCreateMediaCollectionModal}
        onClose={onCloseCreateMediaCollectionModal}
      />
      <ShareAssetModal
        isOpen={isOpenShareAssetModal}
        onClose={onCloseShareAssetModal}
        assetId={shareFolderResponse?.folderId as string}
        assetName={shareFolderResponse?.name as string}
        assetType={shareFolderResponse?.type as string}
      />
      <ShareMediaCollectionModal
        isOpen={isOpenShareMediaCollectionModal}
        onClose={onCloseShareMediaCollectionModal}
        assetId={
          (viewableParent as ViewableParentSuccessResponse)?.mediaCollection
            ?.mediaCollectionId as string
        }
        assetName={
          (viewableParent as ViewableParentSuccessResponse)?.mediaCollection
            ?.name as string
        }
      />
      {isOpenSaveSearchModal && (
        <SaveSearchModal
          isOpen={isOpenSaveSearchModal}
          onClose={onCloseSaveSearchModal}
        />
      )}
    </>
  )
}

export default HeaderToolbar
